import { company } from '../data/site'

/** Navy og varm off-white fra LYKKEVÆRK-paletten. */
const THEME_COLOR = '#16232F'
const BACKGROUND_COLOR = '#F5F4F1'

/** Åbnet som installeret app (hjemmeskærm / installeret PWA) frem for en browserfane. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/**
 * Tegner logomærket centreret på en navy flade som kvadratisk PNG.
 * Falder tilbage til selve logo-filen, hvis billedet ikke kan tegnes.
 */
function renderIcon(image: HTMLImageElement | null, size: number, padding: number): string {
  if (!image) return company.logoMark
  try {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return company.logoMark
    ctx.fillStyle = THEME_COLOR
    ctx.fillRect(0, 0, size, size)
    const inner = size * (1 - padding * 2)
    const scale = Math.min(inner / image.naturalWidth, inner / image.naturalHeight)
    const width = image.naturalWidth * scale
    const height = image.naturalHeight * scale
    ctx.drawImage(image, (size - width) / 2, (size - height) / 2, width, height)
    return canvas.toDataURL('image/png')
  } catch {
    return company.logoMark
  }
}

function loadLogo(): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = company.logoMark
  })
}

function setMeta(name: string, content: string, created: Element[]) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
    created.push(tag)
  }
  tag.content = content
}

/**
 * Manifest og app-tags sættes KUN på admin-siderne, så den offentlige
 * hjemmeside er uændret. Returnerer en oprydningsfunktion.
 */
export function applyAdminAppMeta(): () => void {
  const created: Element[] = []
  let cancelled = false
  const touchIcon = document.head.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  const previousTouchIcon = touchIcon?.href ?? null

  setMeta('theme-color', THEME_COLOR, created)
  setMeta('apple-mobile-web-app-capable', 'yes', created)
  setMeta('mobile-web-app-capable', 'yes', created)
  setMeta('apple-mobile-web-app-title', 'LYKKEVÆRK', created)
  setMeta('apple-mobile-web-app-status-bar-style', 'black-translucent', created)

  void loadLogo().then((image) => {
    if (cancelled) return
    const origin = window.location.origin
    const manifest = {
      id: `${origin}/admin/`,
      name: 'LYKKEVÆRK Admin',
      short_name: 'LYKKEVÆRK',
      start_url: `${origin}/admin/i-dag`,
      scope: `${origin}/admin/`,
      display: 'standalone',
      orientation: 'portrait',
      theme_color: THEME_COLOR,
      background_color: BACKGROUND_COLOR,
      lang: 'da',
      icons: [
        { src: renderIcon(image, 192, 0.14), sizes: '192x192', type: 'image/png', purpose: 'any' },
        { src: renderIcon(image, 512, 0.14), sizes: '512x512', type: 'image/png', purpose: 'any' },
        { src: renderIcon(image, 512, 0.22), sizes: '512x512', type: 'image/png', purpose: 'maskable' },
      ],
    }
    const link = document.createElement('link')
    link.rel = 'manifest'
    link.href = `data:application/manifest+json;charset=utf-8,${encodeURIComponent(JSON.stringify(manifest))}`
    document.head.querySelector('link[rel="manifest"]')?.remove()
    document.head.appendChild(link)
    created.push(link)

    const appleIcon = renderIcon(image, 180, 0.14)
    if (touchIcon) {
      touchIcon.href = appleIcon
    } else {
      const icon = document.createElement('link')
      icon.rel = 'apple-touch-icon'
      icon.setAttribute('sizes', '180x180')
      icon.href = appleIcon
      document.head.appendChild(icon)
      created.push(icon)
    }
  })

  return () => {
    cancelled = true
    created.forEach((element) => element.remove())
    if (touchIcon && previousTouchIcon) touchIcon.href = previousTouchIcon
  }
}

/** Den ENE service worker: Firebase Messaging + offline-skal for admin. */
export const SW_URL = '/firebase-messaging-sw.js'
export const SW_SCOPE = '/'

export interface SwDiagnostics {
  scriptUrl: string
  scope: string
  https: boolean
  standalone: boolean
  /** HTTP-status for /firebase-messaging-sw.js, eller null hvis den ikke kunne hentes. */
  httpStatus: number | null
  contentType: string
  /** 'javascript' | 'html' (fx 404- eller fallback-side) | 'unknown' */
  scriptKind: 'javascript' | 'html' | 'unknown'
  registrationError?: string
  activeScope?: string
}

export interface SwResult {
  registration: ServiceWorkerRegistration | null
  diagnostics: SwDiagnostics
  /** Læsbar årsag, når registreringen fejler. */
  error?: string
}

let pending: Promise<SwResult> | null = null

/** Tjekker om filen faktisk returnerer JavaScript — ikke sidens HTML eller en 404. */
async function inspectScript(diagnostics: SwDiagnostics) {
  try {
    const response = await fetch(SW_URL, { cache: 'no-store' })
    diagnostics.httpStatus = response.status
    diagnostics.contentType = response.headers.get('content-type') ?? ''
    const head = (await response.text()).slice(0, 300).trimStart().toLowerCase()
    if (/javascript|ecmascript/.test(diagnostics.contentType) && !head.startsWith('<')) diagnostics.scriptKind = 'javascript'
    else if (head.startsWith('<!doctype') || head.startsWith('<html') || /text\/html/.test(diagnostics.contentType))
      diagnostics.scriptKind = 'html'
    else diagnostics.scriptKind = head.includes('importscripts') ? 'javascript' : 'unknown'
  } catch (error) {
    diagnostics.httpStatus = null
    diagnostics.registrationError = `fetch: ${(error as Error)?.message ?? String(error)}`
  }
}

/** Fjerner ældre workers (fx /sw.js med scope /admin/), så der aldrig er to i konflikt. */
async function removeOldWorkers() {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations
        .filter((item) => {
          const script = item.active?.scriptURL ?? item.waiting?.scriptURL ?? item.installing?.scriptURL ?? ''
          return script && !script.endsWith(SW_URL)
        })
        .map((item) => item.unregister()),
    )
  } catch {
    // Ikke kritisk
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms)
    promise.then(
      (value) => {
        window.clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        window.clearTimeout(timer)
        reject(error)
      },
    )
  })
}

/**
 * Registrerer /firebase-messaging-sw.js (scope "/") og venter på
 * navigator.serviceWorker.ready. Ved fejl returneres den reelle årsag og
 * diagnosticering (HTTP-status, JS eller HTML, https, standalone).
 * En fejlet registrering huskes ikke, så "Prøv igen" forsøger forfra.
 */
export function registerAdminServiceWorker(): Promise<SwResult> {
  if (pending) return pending
  pending = (async (): Promise<SwResult> => {
    const diagnostics: SwDiagnostics = {
      scriptUrl: new URL(SW_URL, window.location.origin).href,
      scope: SW_SCOPE,
      https: window.isSecureContext && window.location.protocol === 'https:',
      standalone: isStandalone(),
      httpStatus: null,
      contentType: '',
      scriptKind: 'unknown',
    }

    if (!('serviceWorker' in navigator)) {
      return { registration: null, diagnostics, error: 'Service workers findes ikke i denne browser.' }
    }
    if (!window.isSecureContext) {
      return { registration: null, diagnostics, error: 'Siden kører ikke over HTTPS.' }
    }

    // Forhåndstjek: registrér KUN, hvis filen faktisk er JavaScript — ikke sidens HTML/404.
    await inspectScript(diagnostics)
    if (diagnostics.scriptKind !== 'javascript') {
      const reason =
        diagnostics.scriptKind === 'html'
          ? `${SW_URL} er ikke publiceret: adressen returnerer hjemmesidens HTML (HTTP ${diagnostics.httpStatus ?? '?'}) i stedet for JavaScript.`
          : diagnostics.httpStatus === null
            ? `${SW_URL} kunne ikke hentes.`
            : `${SW_URL} svarer HTTP ${diagnostics.httpStatus} uden gyldigt JavaScript.`
      // eslint-disable-next-line no-console
      console.error('[PWA] service worker ikke publiceret', { reason, ...diagnostics })
      pending = null
      return { registration: null, diagnostics, error: reason }
    }

    try {
      await removeOldWorkers()
      const registration = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE })
      await withTimeout(navigator.serviceWorker.ready, 15_000, 'navigator.serviceWorker.ready svarede ikke inden for 15 sek.')
      diagnostics.activeScope = registration.scope
      return { registration, diagnostics }
    } catch (error) {
      diagnostics.registrationError = `${(error as Error)?.name ?? 'Error'}: ${(error as Error)?.message ?? String(error)}`
      const reason = diagnostics.registrationError
      // eslint-disable-next-line no-console
      console.error('[PWA] service worker fejlede', { reason, ...diagnostics })
      pending = null
      return { registration: null, diagnostics, error: reason }
    }
  })()
  return pending
}
